import { Controller } from "@hotwired/stimulus"
import { track } from "../lib/mixpanel"

export default class extends Controller {
  static targets = ["pulseText", "pulseIcon", "pulseTitle", "pulseDate", "regenerateBtn", "regenerateIcon", "regenerateSpinner"]
  static values  = { projectId: Number, regenerateUrl: String, projectPhase: String }

  connect() {
    track("Project Viewed", { project_id: this.projectIdValue, phase: this.projectPhaseValue })
  }

  get _csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content
  }

  async regeneratePulse() {
    // Disable button, activate rainbow shimmer
    this.regenerateIconTarget.classList.add("hidden")
    this.regenerateSpinnerTarget.classList.remove("hidden")
    this.regenerateBtnTarget.disabled = true
    this.regenerateBtnTarget.classList.add("opacity-50", "pointer-events-none")
    this.pulseIconTarget.classList.remove("ai-gradient-icon")
    this.pulseIconTarget.classList.add("ai-rainbow-icon")
    this.pulseIconTarget.style.color = ""
    this.pulseTitleTarget.classList.remove("ai-gradient-text")
    this.pulseTitleTarget.classList.add("ai-rainbow-text")

    // Show animated progress steps in the pulse text area
    this._showProgressSteps()

    try {
      const resp = await fetch(this.regenerateUrlValue, {
        method: "POST",
        headers: {
          "X-CSRF-Token": this._csrfToken,
          "Accept": "application/json"
        }
      })

      this._stopProgressSteps()

      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}))
        throw new Error(data.error || "Failed to generate summary")
      }

      const data = await resp.json()

      // Update the pulse text and date directly in the DOM
      this.pulseTextTarget.innerHTML = `<p class="text-[13px] text-gray-600 leading-relaxed whitespace-pre-line">${this._escapeHtml(data.response)}</p>`
      if (this.hasPulseDateTarget && data.updated_at) {
        this.pulseDateTarget.textContent = data.updated_at
      }

      this._resetRegenerateBtn()
      this._showToast("Project Pulse updated")
    } catch (e) {
      this._stopProgressSteps()
      this._resetRegenerateBtn()
      this._showToast(e.message || "Failed to regenerate summary", "error")
    }
  }

  // ── Photo lightbox ─────────────────────────────────────────────────

  openLightbox(event) {
    const index = parseInt(event.currentTarget.dataset.photoIndex, 10)
    const photos = window._dashboardPhotos || []
    if (!photos.length) return

    const lightboxEl = document.querySelector('[data-controller~="photo-lightbox"]')
    if (!lightboxEl) return
    const lightboxCtrl = this.application.getControllerForElementAndIdentifier(lightboxEl, "photo-lightbox")
    if (lightboxCtrl) {
      lightboxCtrl.open({ photos, index })
    }
  }

  // ── Progress steps animation ──────────────────────────────────────

  _showProgressSteps() {
    this._savedPulseHtml = this.pulseTextTarget.innerHTML

    const steps = [
      { text: "Gathering project data...", icon: "database" },
      { text: "Analyzing schedule & progress...", icon: "calendar" },
      { text: "Reviewing daily reports...", icon: "document" },
      { text: "Checking action items...", icon: "checklist" },
      { text: "Analyzing financial data...", icon: "dollar" },
      { text: "Generating briefing...", icon: "sparkle" }
    ]

    const container = document.createElement("div")
    container.className = "space-y-2 py-2"
    container.dataset.progressSteps = "true"

    this.pulseTextTarget.innerHTML = ""
    this.pulseTextTarget.appendChild(container)

    this._progressTimers = []
    steps.forEach((step, i) => {
      const timer = setTimeout(() => {
        const row = document.createElement("div")
        row.className = "flex items-center gap-2 text-[13px] text-gray-500 animate-fade-in"
        row.style.animation = "fadeIn 0.3s ease-in"
        row.innerHTML = `
          <svg class="w-3.5 h-3.5 ai-rainbow-icon shrink-0" viewBox="0 0 24 24" fill="currentColor"><path d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z"/></svg>
          <span>${step.text}</span>
        `
        container.appendChild(row)
      }, i * 1500)
      this._progressTimers.push(timer)
    })
  }

  _stopProgressSteps() {
    if (this._progressTimers) {
      this._progressTimers.forEach(t => clearTimeout(t))
      this._progressTimers = null
    }
  }

  // ── Reset ─────────────────────────────────────────────────────────

  _resetRegenerateBtn() {
    this.regenerateIconTarget.classList.remove("hidden")
    this.regenerateSpinnerTarget.classList.add("hidden")
    this.regenerateBtnTarget.disabled = false
    this.regenerateBtnTarget.classList.remove("opacity-50", "pointer-events-none")
    this.pulseIconTarget.classList.remove("ai-rainbow-icon")
    this.pulseIconTarget.classList.add("ai-gradient-icon")
    this.pulseIconTarget.style.color = ""
    this.pulseTitleTarget.classList.remove("ai-rainbow-text")
    this.pulseTitleTarget.classList.add("ai-gradient-text")
  }

  // ── Helpers ───────────────────────────────────────────────────────

  _escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }

  _showToast(message, type = "success") {
    const cssType = (type === "error" || type === "alert") ? "alert" : "notice"
    const icon = cssType === "notice"
      ? '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/></svg>'
      : '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/></svg>'
    const el = document.createElement("div")
    el.className = `flash-toast-${cssType}`
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "5000")
    el.setAttribute("role", "alert")
    const iconDiv = document.createElement("div")
    iconDiv.className = "shrink-0"
    iconDiv.innerHTML = icon
    const msgP = document.createElement("p")
    msgP.className = "flex-1 text-sm"
    msgP.textContent = message
    el.appendChild(iconDiv)
    el.appendChild(msgP)
    document.body.prepend(el)
  }
}
